<template>
  <!-- `embedded` renders this view as a Reports tab body: no shell, no page heading of its own. -->
  <component :is="embedded ? 'div' : AppShell">
    <section class="view on impact-view" :aria-labelledby="embedded ? undefined : 'impact-title'" :aria-label="embedded ? t('impact_h') : undefined">
      <div class="pagehead" :class="{ 'ph-embedded': embedded }">
        <div v-if="!embedded">
          <h1 id="impact-title">{{ t("impact_h") }}</h1>
          <p class="sub">{{ t("impact_sub") }}</p>
        </div>
        <div v-if="comparable" class="page-actions">
          <a class="askmaya-chip" :href="csvUrl" download>{{ t("impact_export") }}</a>
        </div>
      </div>

      <p v-if="loading" class="empty" role="status">{{ t("loading") }}</p>
      <p v-else-if="error" class="empty" role="alert">{{ error }} <button type="button" class="atbtn" @click="load">{{ t("account_retry") }}</button></p>
      <p v-else-if="!currentBrand" class="empty">{{ t("ins_none") }}</p>

      <!-- No re-test at all: there is nothing to compare and nothing to explain away. Send them to
           the one person who can change that. -->
      <div v-else-if="noRuns" class="impact-empty">
        <h2 class="ie-ttl">{{ t("impact_empty_h") }}</h2>
        <p class="ie-sub">{{ t("impact_empty_sub") }}</p>
        <button type="button" class="btn" @click="askMaya">{{ t("impact_empty_cta") }}</button>
      </div>

      <template v-else>
        <section class="inscard impact-head" aria-labelledby="impact-verdict">
          <p class="ih-top">
            <span class="ih-state" :class="`is-${payload.status}`">{{ stateLabel }}</span>
            <span v-if="payload.baseline?.date && payload.now?.date" class="ih-window">{{ t("impact_window", { from: payload.baseline.date, to: payload.now.date }) }}</span>
          </p>
          <h2 id="impact-verdict" class="ih-say">{{ headline }}</h2>
          <p v-if="engineCaveat" class="ih-caveat">{{ engineCaveat }}</p>
          <p v-if="granularityCaveat" class="ih-caveat">{{ granularityCaveat }}</p>

          <div v-if="baselineOptions.length" class="ih-baseline">
            <label :for="'impact-baseline'">{{ t("impact_baseline") }}</label>
            <select id="impact-baseline" :value="String(payload.baseline?.run_id || '')" :disabled="savingBaseline" @change="changeBaseline">
              <option v-if="!payload.baseline?.run_id" value="" disabled>{{ t("impact_baseline_none") }}</option>
              <option v-for="run in baselineOptions" :key="run.run_id" :value="String(run.run_id)">{{ run.date }}<template v-if="run.score != null"> · {{ run.score }}/100</template></option>
            </select>
            <span v-if="baselineError" class="ih-berr" role="alert">{{ baselineError }}</span>
          </div>
        </section>

        <div v-if="comparable" class="impact-metrics">
          <article v-for="metric in metricCards" :key="metric.key" class="inscard metric-card">
            <h3>{{ metric.title }}</h3>
            <template v-if="metric.data?.status === 'ok'">
              <p class="mc-pair">
                <span class="mc-was"><b>{{ pct(metric.data.baseline.pct) }}</b><i>{{ t("impact_before") }}</i></span>
                <span class="mc-arrow" aria-hidden="true">→</span>
                <span class="mc-now" :class="moveClass(metric.data)"><b>{{ pct(metric.data.now.pct) }}</b><i>{{ t("impact_now") }}</i></span>
              </p>
              <p class="mc-counts">{{ t("impact_counts", { now: metric.data.now.n, total: metric.data.now.total, was: metric.data.baseline.n }) }}</p>
              <p v-if="metric.data.now.ci" class="mc-ci">{{ t("impact_ci", { lo: metric.data.now.ci[0], hi: metric.data.now.ci[1] }) }}</p>
              <p v-if="metric.data.note" class="mc-note">{{ metric.data.note }}</p>
            </template>
            <!-- A metric with no denominator has no number. Never a 0, never an empty bar. -->
            <template v-else>
              <p class="mc-unknown">{{ t("impact_not_measured") }}</p>
              <p class="mc-note">{{ metric.data?.note || t("impact_unknown_note") }}</p>
            </template>
          </article>
        </div>

        <p v-if="competitorLine" class="impact-rival">{{ competitorLine }}</p>

        <section v-if="perQuestion.length" class="inscard" aria-labelledby="impact-questions-title">
          <h3 id="impact-questions-title">{{ t("impact_q_h") }}</h3>
          <p class="csub">{{ t("impact_q_sub") }}</p>
          <div class="tablewrap">
            <table class="rtable impact-table">
              <thead>
                <tr>
                  <th scope="col">{{ t("impact_col_q") }}</th>
                  <th scope="col">{{ t("impact_col_before") }}</th>
                  <th scope="col">{{ t("impact_col_now") }}</th>
                  <th scope="col">{{ t("impact_col_change") }}</th>
                  <th scope="col">{{ t("impact_col_pages") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in perQuestion" :key="row.question_id">
                  <th scope="row">{{ row.q }}</th>
                  <td class="num">{{ recText(row.baseline_rec) }}</td>
                  <td class="num">{{ recText(row.now_rec) }}</td>
                  <td><span class="delta" :class="`d-${row.delta}`">{{ deltaLabel(row.delta) }}</span></td>
                  <td>
                    <template v-if="pagesFor(row.question_id).length">
                      <template v-for="page in pagesFor(row.question_id)" :key="page.url">
                        <a v-if="pageHref(page)" class="qpage" :href="pageHref(page)" target="_blank" rel="noopener">{{ pageLabel(page) }}<span class="sr-only"> {{ t("opens_new") }}</span></a>
                        <span v-else class="qpage qpage-plain">{{ pageLabel(page) }}</span>
                      </template>
                    </template>
                    <span v-else class="qnone">—</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section v-if="citedPages.length" class="inscard" aria-labelledby="impact-pages-title">
          <h3 id="impact-pages-title">{{ t("impact_pages_h") }}</h3>
          <ul class="cited-list">
            <li v-for="page in citedPages" :key="page.url">
              <a v-if="pageHref(page)" :href="pageHref(page)" target="_blank" rel="noopener">{{ pageLabel(page) }}<span class="sr-only"> {{ t("opens_new") }}</span></a>
              <span v-else class="cp-plain">{{ pageLabel(page) }}</span>
              <span class="cp-meta">{{ t("impact_page_meta", { n: page.question_ids?.length || 0 }) }}<template v-if="page.engines?.length"> · {{ page.engines.join(", ") }}</template></span>
            </li>
          </ul>
        </section>
      </template>
    </section>
  </component>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";

import { geoSiteEffectCsvUrl, getGeoSiteEffect, listBaselineRuns, setBaselineRun } from "../api/geoExecR5";
import AppShell from "../layout/AppShell.vue";
import { currentBrand, loadBrands } from "../stores/brands";
import { useI18n } from "../useI18n";

defineProps({ embedded: { type: Boolean, default: false } });

const router = useRouter();
const { t } = useI18n();
const payload = ref({}), runs = ref([]), loading = ref(true), error = ref("");
const savingBaseline = ref(false), baselineError = ref("");
let controller;

const comparable = computed(() => payload.value.status === "comparable");
const noRuns = computed(() => payload.value.reason === "no_runs" || (payload.value.status === "collecting" && !payload.value.now?.run_id));
const metrics = computed(() => payload.value.metrics || {});
const perQuestion = computed(() => payload.value.per_question || []);
const citedPages = computed(() => payload.value.cited_pages || []);
const csvUrl = computed(() => geoSiteEffectCsvUrl(currentBrand.value?.id || ""));
const baselineOptions = computed(() => (runs.value || []).filter((run) => run.selectable_as_baseline || String(run.run_id) === String(payload.value.baseline?.run_id || "")));

const stateLabel = computed(() => t({ comparable: "impact_st_measured", collecting: "impact_st_collecting", not_comparable: "impact_st_not_comparable" }[payload.value.status] || "impact_st_collecting"));

const headline = computed(() => {
  if (!comparable.value) return payload.value.message || t("impact_st_collecting");
  const mentioned = metrics.value.mentioned;
  if (mentioned?.status !== "ok") return t("impact_say_no_rate");
  const move = mentioned.now.n - mentioned.baseline.n;
  const key = move > 0 ? "impact_say_up" : move < 0 ? "impact_say_down" : "impact_say_flat";
  return t(key, { total: payload.value.questions_total || mentioned.now.total, now: mentioned.now.n, was: mentioned.baseline.n });
});

// "Same questions" is half the claim; the engine mix is the other half and it drifts, so a
// difference is stated as a caveat rather than swallowed.
const engineCaveat = computed(() => {
  if (!comparable.value) return "";
  const engines = payload.value.engines || {};
  if (engines.same === true) return "";
  if (engines.same === "unknown") return t("impact_engines_unknown");
  return t("impact_engines_differ", { was: (engines.baseline || []).join(", ") || "—", now: (engines.now || []).join(", ") || "—" });
});

const granularityCaveat = computed(() => (comparable.value && payload.value.url_granularity === "domain_only" ? t("impact_domain_only") : ""));

const metricCards = computed(() => [
  { key: "mentioned", title: t("impact_card_mentioned"), data: metrics.value.mentioned },
  { key: "citing_geo_site", title: t("impact_card_citing"), data: metrics.value.citing_geo_site },
]);

const competitorLine = computed(() => {
  const rival = metrics.value.competitor_rate;
  if (!rival || !comparable.value) return "";
  if (rival.status === "ok") return t("impact_rival", { name: rival.competitor, was: pct(rival.baseline.pct), now: pct(rival.now.pct) });
  return rival.note || "";
});

function pct(value) { return `${Number(value || 0).toFixed(0)}%`; }
function recText(value) { return value == null ? t("impact_not_measured") : pct(value); }
function deltaLabel(delta) { return t({ improved: "impact_d_up", regressed: "impact_d_down", unchanged: "impact_d_flat", unknown: "impact_not_measured" }[delta] || "impact_not_measured"); }
function moveClass(metric) { const move = metric.now.pct - metric.baseline.pct; return move > 0 ? "up" : move < 0 ? "down" : ""; }
function pagesFor(questionId) { return citedPages.value.filter((page) => (page.question_ids || []).includes(questionId)); }
function pageLabel(page) { return page.title || String(page.url || "").replace(/^https?:\/\//, "").replace(/\/$/, ""); }
// A citation URL is data that reached us from an engine's answer. Anything that is not plain
// http(s) is rendered as text rather than handed to an href.
function pageHref(page) { return /^https?:\/\//i.test(String(page?.url || "")) ? String(page.url) : ""; }
function askMaya() { router.push("/maya"); }

// Every branch is guarded on `mine === controller`: an aborted load must not turn off the spinner
// a newer one turned on, nor write its brand's numbers over the brand now on screen. The panel is
// cleared up front for the same reason — a stale before/after under a fresh spinner reads as this
// brand's result.
async function load() {
  controller?.abort();
  const mine = (controller = new AbortController());
  payload.value = {};
  runs.value = [];
  loading.value = true;
  error.value = "";
  baselineError.value = "";
  try {
    await loadBrands({ signal: mine.signal });
    const brandId = currentBrand.value?.id;
    if (!brandId) return;
    const effect = await getGeoSiteEffect(brandId, { signal: mine.signal });
    const history = (await listBaselineRuns(brandId, { signal: mine.signal }).catch(() => ({}))).runs || [];
    if (controller !== mine) return;
    payload.value = effect;
    runs.value = history;
  } catch (caught) {
    if (controller === mine && caught.name !== "AbortError") error.value = caught.message || t("impact_load_error");
  } finally {
    if (controller === mine) loading.value = false;
  }
}

async function changeBaseline(event) {
  const runId = Number(event.target.value);
  if (!runId || !currentBrand.value?.id) return;
  savingBaseline.value = true;
  baselineError.value = "";
  try {
    await setBaselineRun(currentBrand.value.id, runId);
    await load();
  } catch (caught) {
    // 409 panel_mismatch / baseline_is_latest both carry the sentence to show.
    baselineError.value = caught?.payload?.message || caught.message || t("impact_baseline_error");
  } finally {
    savingBaseline.value = false;
  }
}

watch(() => currentBrand.value?.id, (next, previous) => { if (next && next !== previous) load(); });
onMounted(load);
onBeforeUnmount(() => controller?.abort());
</script>

<style scoped>
.impact-view { max-width: 1040px; }
/* Embedded, the tab rail above already names the section, so the head keeps only the export chip. */
.ph-embedded { justify-content: flex-end; margin-bottom: 0; }
.page-actions { display: flex; align-items: center; gap: 14px; }
.askmaya-chip { display: inline-flex; align-items: center; gap: 7px; padding: 7px 15px; border: 1px solid var(--line); border-radius: 100px; background: var(--card); color: var(--ink); font-family: inherit; font-size: var(--fs-footnote); font-weight: 600; cursor: pointer; text-decoration: none; transition: border-color var(--dur-press) var(--ease-out); }
.askmaya-chip:hover { border-color: var(--indigo); }
.askmaya-chip:focus-visible { outline: 3px solid var(--indigo); outline-offset: 2px; }

.impact-empty { text-align: center; padding: 56px 20px; }
.ie-ttl { font-size: var(--fs-large); letter-spacing: var(--ls-title); margin-bottom: 8px; }
.ie-sub { color: var(--mute); font-size: var(--fs-subhead); max-width: 44ch; margin: 0 auto 22px; }

.impact-head { display: grid; gap: 10px; }
.ih-top { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
/* The state capsule carries the only thing the client must not misread: whether there IS a
   comparison. Colour follows the app's semantic pairs; the words carry it on their own. */
.ih-state { display: inline-flex; align-items: center; padding: 4px 11px; border-radius: var(--r-pill); font-size: var(--fs-caption2); font-weight: 700; letter-spacing: .04em; text-transform: uppercase; border: 1px solid var(--line); color: var(--mute); background: var(--card); }
.ih-state.is-comparable { color: var(--jade); border-color: var(--ok-line); background: var(--ok-bg); }
.ih-state.is-collecting { color: var(--amber); border-color: var(--warn-line); background: var(--warn-bg); }
.ih-window { font-size: var(--fs-caption); color: var(--mute); font-variant-numeric: tabular-nums; }
.ih-say { font-size: var(--fs-body); font-weight: 600; line-height: 1.5; }
.ih-caveat { font-size: var(--fs-caption); color: var(--mute); line-height: 1.55; }
.ih-baseline { display: flex; align-items: center; gap: 9px; flex-wrap: wrap; font-size: var(--fs-caption); color: var(--mute); }
.ih-baseline select { font-family: inherit; font-size: var(--fs-caption); padding: 6px 10px; border: 1px solid var(--line); border-radius: 10px; background: var(--card); color: var(--ink); }
.ih-berr { color: var(--verm); }

.impact-metrics { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 18px; }
.metric-card h3 { font-size: var(--fs-body); margin-bottom: 14px; }
.mc-pair { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.mc-pair b { font-size: var(--fs-display); font-weight: 700; letter-spacing: var(--ls-display); font-variant-numeric: tabular-nums; }
.mc-pair i { display: block; font-style: normal; font-size: var(--fs-caption2); text-transform: uppercase; letter-spacing: .06em; color: var(--mute); }
.mc-was b { color: var(--mute); }
.mc-arrow { color: var(--mute); }
.mc-now.up b { color: var(--jade); }
.mc-now.down b { color: var(--verm); }
.mc-counts { margin-top: 10px; font-size: var(--fs-caption); color: var(--body); font-variant-numeric: tabular-nums; }
.mc-ci, .mc-note { margin-top: 6px; font-size: var(--fs-caption); color: var(--mute); line-height: 1.55; }
.mc-unknown { font-size: var(--fs-large); font-weight: 700; color: var(--mute); }

.impact-rival { margin: -6px 0 22px; font-size: var(--fs-caption); color: var(--mute); }

/* `position: relative` is load-bearing: the .sr-only spans inside the links are absolutely
   positioned, and without a containing block here they resolve against the page and push the
   document 170px wide at 390px. */
.tablewrap { position: relative; overflow-x: auto; }
.impact-table { min-width: 640px; }
/* .rtable's th is a COLUMN header: uppercase, tracked, and underlined in ink. A row header is a
   question, so it takes the body's treatment back. */
.impact-table th[scope="row"] { font-weight: 500; font-size: var(--fs-subhead); color: var(--ink); text-align: left; text-transform: none; letter-spacing: normal; border-bottom: 1px solid var(--separator); vertical-align: top; }
.impact-table tr:last-child th[scope="row"] { border-bottom: none; }
.impact-table td { vertical-align: top; }
.impact-table td.num { font-variant-numeric: tabular-nums; white-space: nowrap; }
.delta { font-size: var(--fs-caption); font-weight: 600; white-space: nowrap; }
.delta.d-improved { color: var(--jade); }
.delta.d-regressed { color: var(--verm); }
.delta.d-unchanged, .delta.d-unknown { color: var(--mute); }
.qpage { display: block; color: var(--indigo); font-size: var(--fs-caption); }
.qpage-plain, .cp-plain { color: var(--body); }
.qnone { color: var(--mute); }

.cited-list { list-style: none; display: grid; gap: 11px; }
.cited-list li { display: grid; gap: 3px; padding-bottom: 11px; border-bottom: 1px solid var(--separator); }
.cited-list li:last-child { border-bottom: none; padding-bottom: 0; }
.cited-list a { color: var(--indigo); font-size: var(--fs-subhead); }
.cp-meta { font-size: var(--fs-caption); color: var(--mute); }

@media (max-width: 560px) {
  .mc-pair b { font-size: var(--fs-large); }
}
</style>
